import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  TextField,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../config/firebase';
import { updateCase, getCase } from '../../../services/firebase';
import { Document } from '../../../types';
import { Timestamp } from 'firebase/firestore';
import { formatFileSize } from '../../../utils/formatters';
import { useDropzone } from 'react-dropzone';

interface DocumentUploadProps {
  caseId: string;
  onSuccess?: () => void;
}

interface FileUpload {
  file: File;
  customName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'error' | 'success';
  error?: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ caseId, onSuccess }) => {
  const theme = useTheme();
  const [fileUploads, setFileUploads] = useState<FileUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      customName: file.name.split('.')[0],
      progress: 0,
      status: 'pending' as const,
    }));
    setFileUploads(prev => [...prev, ...newFiles]);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt']
    }
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files).map(file => ({
        file,
        customName: file.name.split('.')[0],
        progress: 0,
        status: 'pending' as const,
      }));
      setFileUploads(prev => [...prev, ...newFiles]);
      setError(null);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFileUploads(prev => prev.filter((_, i) => i !== index));
  };

  const handleCustomNameChange = (index: number, newName: string) => {
    setFileUploads(prev => prev.map((upload, i) => 
      i === index ? { ...upload, customName: newName } : upload
    ));
  };

  const uploadFile = async (fileUpload: FileUpload, index: number): Promise<Document | null> => {
    const { file, customName } = fileUpload;
    
    try {
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || '';
      const cleanFileName = customName.replace(/[^a-zA-Z0-9]/g, '_');
      const finalFileName = `${cleanFileName}_${timestamp}.${fileExtension}`;
      const filePath = `cases/${caseId}/${finalFileName}`;
      
      const storageRef = ref(storage, filePath);
      
      const metadata = {
        contentType: file.type,
        customMetadata: {
          fileName: file.name,
          timestamp: timestamp.toString(),
          caseId
        }
      };
      
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setFileUploads(prev => prev.map((upload, i) => 
              i === index ? { ...upload, progress, status: 'uploading' } : upload
            ));
          },
          (error) => {
            setFileUploads(prev => prev.map((upload, i) => 
              i === index ? { ...upload, status: 'error', error: 'Upload failed' } : upload
            ));
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(storageRef);
              const newDoc: Document = {
                id: `doc_${timestamp}`,
                name: customName,
                type: file.type,
                size: file.size,
                url: downloadURL,
                path: filePath,
                createdAt: Timestamp.now()
              };
              setFileUploads(prev => prev.map((upload, i) => 
                i === index ? { ...upload, status: 'success' } : upload
              ));
              resolve(newDoc);
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    } catch (err) {
      setFileUploads(prev => prev.map((upload, i) => 
        i === index ? { ...upload, status: 'error', error: 'Upload failed' } : upload
      ));
      return null;
    }
  };

  const handleUpload = async () => {
    if (fileUploads.length === 0) {
      setError('Please select files to upload');
      return;
    }

    const invalidFiles = fileUploads.filter(upload => !upload.customName.trim());
    if (invalidFiles.length > 0) {
      setError('All files must have names');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = fileUploads.map((upload, index) => uploadFile(upload, index));
      const documents = await Promise.all(uploadPromises);
      const validDocuments = documents.filter((doc): doc is Document => doc !== null);

      if (validDocuments.length > 0) {
        const currentCase = await getCase(caseId);
        const existingDocuments = currentCase.documents || [];
        
        const updatedDocuments = [...existingDocuments, ...validDocuments];

        await updateCase(caseId, {
          documents: updatedDocuments
        });

        if (onSuccess) {
          onSuccess();
        }
        window.location.reload();
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Some files failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <Box
            {...getRootProps()}
            sx={{
              border: `2px dashed ${alpha(theme.palette.primary.main, isDragActive ? 0.5 : 0.2)}`,
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: alpha(theme.palette.primary.main, 0.5),
                mb: 2,
              }}
            />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {isDragActive ? 'Drop the files here' : 'Drag and drop files here'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to select files
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              Supported formats: PDF, Images, Word documents, Excel sheets, Text files
            </Typography>
          </Box>

          <List>
            {fileUploads.map((fileUpload, index) => (
              <ListItem key={index} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={fileUpload.customName}
                    onChange={(e) => handleCustomNameChange(index, e.target.value)}
                    disabled={uploading || fileUpload.status === 'success'}
                    error={!fileUpload.customName.trim()}
                    helperText={!fileUpload.customName.trim() ? 'Name is required' : ''}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      {formatFileSize(fileUpload.file.size)}
                    </Typography>
                    {fileUpload.status === 'uploading' && (
                      <LinearProgress variant="determinate" value={fileUpload.progress} />
                    )}
                    {fileUpload.status === 'error' && (
                      <Typography color="error" variant="caption">
                        {fileUpload.error}
                      </Typography>
                    )}
                    {fileUpload.status === 'success' && (
                      <Typography color="success.main" variant="caption">
                        Upload complete
                      </Typography>
                    )}
                  </Box>
                </Box>
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveFile(index)}
                    disabled={uploading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {fileUploads.length > 0 && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || fileUploads.some(f => !f.customName.trim())}
              fullWidth
            >
              {uploading ? 'Uploading Files...' : 'Upload All Files'}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default DocumentUpload; 